import { FC, useCallback, MouseEventHandler } from 'react';
import { FormattedMessage, useIntl } from 'dibs-react-intl';
import { handleLocaleUrl } from 'dibs-intl/exports/urls';
import { graphql, useFragment, useMutation } from 'react-relay';

import { Button } from 'dibs-elements/exports/Button';
import { trackEvent, eventNameConstants, interactionTypeConstants } from 'dibs-tracking';

import { PdpSharedAddToCartButton_user$key } from './__generated__/PdpSharedAddToCartButton_user.graphql';
import { PdpSharedAddToCartButton_item$key } from './__generated__/PdpSharedAddToCartButton_item.graphql';
import type { PdpSharedAddToCartButtonMutation } from './__generated__/PdpSharedAddToCartButtonMutation.graphql';

const multiItemCartAddMutation = graphql`
    mutation PdpSharedAddToCartButtonMutation($input: UserCartItemAddOrUpdateInput!) {
        userCartItemAddOrUpdate(input: $input) {
            clientMutationId
        }
    }
`;

const userFragment = graphql`
    fragment PdpSharedAddToCartButton_user on User {
        serviceId
    }
`;
const itemFragment = graphql`
    fragment PdpSharedAddToCartButton_item on Item {
        serviceId
        defaultSkuId @required(action: THROW)
    }
`;

export const PdpSharedAddToCartButton: FC<{
    user: PdpSharedAddToCartButton_user$key | null | undefined;
    item: PdpSharedAddToCartButton_item$key;
    skuId?: string;
    quantitySelected?: string;
}> = ({ user: userRef, item: itemRef, skuId, quantitySelected }) => {
    const intl = useIntl();
    const user = useFragment(userFragment, userRef);
    const item = useFragment(itemFragment, itemRef);
    const [addItemToCart, addingItemToCart] =
        useMutation<PdpSharedAddToCartButtonMutation>(multiItemCartAddMutation);
    const { serviceId: itemId, defaultSkuId } = item || {};
    const userId = user?.serviceId;

    const onClick: MouseEventHandler = useCallback(
        event => {
            trackEvent(
                {
                    action: 'pdp add to cart button',
                    category: 'add to cart click',
                    eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
                    interaction_type: interactionTypeConstants.INTERACTION_TYPE_ADD_TO_CART,
                },
                event
            );
            window.location.assign(handleLocaleUrl('/my/cart/', intl.locale));
        },
        [intl.locale]
    );

    return (
        <Button
            fullWidth
            type="secondary"
            size="large"
            onClick={e => {
                if (userId) {
                    addItemToCart({
                        variables: {
                            input: {
                                itemId: itemId || '',
                                skuId: skuId || defaultSkuId,
                                quantity: parseInt(quantitySelected || '', 10) || 1,
                                type: 'CHECKOUT',
                            },
                        },
                    });
                }
                onClick(e);
            }}
            disabled={addingItemToCart}
        >
            <FormattedMessage
                id="PdpSharedAddToCartButton.addToCart"
                defaultMessage="Add to cart"
            />
        </Button>
    );
};
